import { Button, Icon, Modal, Typography } from '@repo/ui';
import c from 'classnames';
import useAgreement from 'hooks/agreements/useAgreement';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useState } from 'react';
import useCreateAgreement from 'services/agreements/useCreateAgreement';
import useImpersonate from 'services/auth/useImpersonate';
import { AgreementKey } from 'types/Agreement';
import styles from './ConditionsInfoModal.module.css';

/**
 * The copy the conditions experience has always shown. Passed rather than read
 * from a flag so that retiring `gut_x_axis` leaves nothing to unpick.
 */
export const CONDITIONS_DISCLAIMER_TITLE =
  'This test does not diagnose any health conditions';
export const CONDITIONS_DISCLAIMER_BODY =
  'This section shows metrics that may be associated with these conditions. Lifestyle, genetics, and other factors may also influence the presence of these conditions.';

type ConditionsInfoModalProps = {
  title?: string;
  body?: string;
};

/**
 * Blocks the conditions experience until the user acknowledges it is not
 * diagnostic, and records that acknowledgement. Required by legal/FDA, so both
 * the old and new conditions home mount it.
 */
export const ConditionsInfoModal = ({
  title = CONDITIONS_DISCLAIMER_TITLE,
  body = CONDITIONS_DISCLAIMER_BODY,
}: ConditionsInfoModalProps = {}) => {
  const [isChecked, setIsChecked] = useState(false);
  const { currentKit, currentKitId } = useCurrentKitAndTinyAccount();
  const tinyAccountId = currentKit?.tinyaccount_id;
  const { impersonateId } = useImpersonate();

  const agreement = useAgreement(AgreementKey.NON_DIAGNOSTIC);

  const {
    mutateAsync: createAgreement,
    isPending,
    isError,
  } = useCreateAgreement();

  const handleProceed = async () => {
    if (!tinyAccountId || !currentKitId) return;

    try {
      await createAgreement({
        tinyaccount_id: tinyAccountId,
        agreement_key: AgreementKey.NON_DIAGNOSTIC,
        kit_id: currentKitId,
      });
    } catch {
      // caught only to keep the rejection from going unhandled; the modal stays
      // open and isError renders the message, since proceeding without a stored
      // agreement is the thing this gate exists to prevent
    }
  };

  return (
    <Modal
      isOpen={!agreement && !impersonateId}
      isDismissable={false}
      className={styles.modal}
    >
      <div className='flex flex-column align-items-center gap-3 text-center'>
        <div className='flex flex-column align-items-center gap-2'>
          <div className={styles.circledIcon}>
            <Icon name='alertTriangleFilled' size='l' />
          </div>
          <Typography
            variant='heading-s'
            className={c('mb-2 font-bold')}
            responsive
          >
            {title}
          </Typography>
        </div>

        <div className='flex flex-column gap-4'>
          <Typography variant='body-xs' className={styles.secondaryText}>
            {body}
          </Typography>
          <div
            className={styles.checkLine}
            onClick={() => setIsChecked(!isChecked)}
          >
            <input type='checkbox' checked={isChecked} readOnly />
            <Typography variant='body-s' responsive>
              I understand this test does not diagnose conditions.
            </Typography>
          </div>

          <Button
            color='blue'
            width={'full'}
            disabled={!isChecked}
            loading={isPending}
            onClick={handleProceed}
          >
            Proceed
          </Button>

          {isError && (
            <Typography variant='body-xs' className={styles.errorText}>
              We could not save your acknowledgement. Please try again.
            </Typography>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ConditionsInfoModal;
